import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { adminService } from '../../services';
import {
  AdminModal, AdminPageHeader, AdminTooltip, Card, DrillList, Loading, SegTrack, StatTile, TileGrid,
  money, useAdminShell,
} from '../../components/admin/shell';
import { BarList, ColumnChart, LineChart, fmt } from '../../components/admin/analytics/charts';

const EVENT_STATUSES = ['draft', 'selling', 'funding', 'processing_greenlight', 'greenlit', 'ended', 'dissolved', 'canceled'];
const RANGES = [7, 30, 90];

/** $12,400 reads as $12.4K on an axis; the tooltip still says the exact number. */
const moneyAxis = (cents) => {
  const d = Math.abs(cents || 0) / 100;
  if (d >= 1_000_000) return `$${(d / 1_000_000).toFixed(1)}M`;
  if (d >= 1000) return `$${(d / 1000).toFixed(d >= 10_000 ? 0 : 1)}K`;
  return `$${Math.round(d)}`;
};
const shortDay = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(iso);
  return `${d.toLocaleString('en', { month: 'short' }).toUpperCase()} ${d.getDate()}`;
};
const asBreakdown = (items = []) => items.map((i) => ({ label: i.label, value: Number(i.value) || 0, display: i.display }));

/**
 * OVERVIEW › STATS — platform totals, then the four charts the handoff asks for. Every
 * tile and every day carries a breakdown ("WHERE IT COMES FROM") and the query it came
 * from, and clicking through opens the rows behind the number: the events that made a
 * day's money, the people who signed up, who put money into an event's escrow.
 *
 * The breakdowns are a second request on purpose — the totals are four counts, the
 * breakdowns are ten group-bys, and the tiles should not wait for them.
 */
export default function StatsPage() {
  const navigate = useNavigate();
  const { can } = useUser();
  const { openPerson, canSeePeople } = useAdminShell();
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [tiles, setTiles] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [signups, setSignups] = useState(null);
  const [escrow, setEscrow] = useState(null);
  const [fees, setFees] = useState(null);
  const [drill, setDrill] = useState(null);

  useEffect(() => {
    (async () => {
      const [s, b, e, f] = await Promise.all([
        adminService.getStats(), adminService.getStatBreakdowns(), adminService.getEscrowByEvent(12), adminService.getFeesBySource(0),
      ]);
      if (s.success) setStats(s.data);
      if (b.success) setTiles(b.data?.tiles || {});
      if (e.success) setEscrow(e.data);
      if (f.success) setFees(f.data);
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [r, g] = await Promise.all([adminService.getRevenue(days), adminService.getSignups(days)]);
      if (cancelled) return;
      if (r.success) setRevenue(r.data);
      if (g.success) setSignups(g.data);
    })();
    return () => { cancelled = true; };
  }, [days]);

  const canOpenEvents = can('events', 'view');
  const openHref = (href) => {
    if (!href) return;
    const person = /[?&]person=([^&]+)/.exec(href);
    if (person) { if (canSeePeople) openPerson(person[1]); return; }
    navigate(href);
  };

  /** The tooltip for one tile, from the breakdown payload. Absent until it lands. */
  const tipFor = (key, title) => {
    const b = tiles?.[key];
    if (!b || b.items.length === 0) return undefined;
    return (
      <AdminTooltip
        title={title}
        desc={b.desc}
        breakdownLabel={b.breakdownLabel}
        breakdown={asBreakdown(b.items)}
        source={b.source}
        drill={b.items.some((i) => i.href) ? 'SEE THE ROWS →' : undefined}
        onDrill={b.items.some((i) => i.href) ? () => setDrill({ title, kicker: b.desc, items: b.items }) : undefined}
      />
    );
  };

  const moneyTiles = stats ? [
    ['WALLET BALANCES', money(stats.totalPsInCirculation), 'What the platform owes users', 'balances'],
    ['$ IN ESCROW', money(stats.totalEscrow), 'Held until an event completes', 'escrow'],
    ['TICKET REVENUE', money(stats.totalGuestCheckoutRevenue), 'Every checkout, all time', 'revenue'],
    ['PORTAL FEES', money(stats.totalServiceFees), 'Service + Party Starter fees, from the ledger', 'fees'],
    ['GST COLLECTED', money(stats.totalGstCollected), 'Held for the CRA', 'gst'],
    ['PAYOUT FEES', money(stats.totalFeesCollected), "Stripe's charge on cash-outs, passed through", 'payoutFees'],
    ['PROJECTED FEES', money(stats.projectedEscrowFees), `${stats.partyStarterFeePercent || 7}% of escrow still held`, 'projectedFees'],
    ['TOTAL WITHDRAWN', money(stats.totalWithdrawalsCompleted), `${(stats.totalWithdrawalCount || 0).toLocaleString()} payouts`, 'withdrawn'],
  ] : [];

  const peopleTiles = stats ? [
    ['TOTAL USERS', (stats.totalUsers || 0).toLocaleString(), `${(stats.usersLast7Days || 0).toLocaleString()} in the last 7 days`, 'users'],
    ['TOTAL EVENTS', (stats.totalEvents || 0).toLocaleString(), `${(stats.eventsLast7Days || 0).toLocaleString()} in the last 7 days`, 'events'],
    ['TOTAL APPLICATIONS', (stats.totalApplications || 0).toLocaleString(), 'Every seat ever asked for', 'applications'],
    ['TOTAL TRANSACTIONS', (stats.totalTransactions || 0).toLocaleString(), `${(stats.transactionsLast7Days || 0).toLocaleString()} in the last 7 days`, 'transactions'],
  ] : [];

  const statusRows = stats ? EVENT_STATUSES.map((status) => ({
    label: status.replace(/_/g, ' ').toUpperCase(),
    count: stats.eventsByStatus?.[status] ?? 0,
    tip: `Every event sitting at ${status.replace(/_/g, ' ')} right now.`,
    source: `events WHERE status = '${status}'`,
    onSelect: canOpenEvents ? () => navigate(`/admin/events?status=${status}`) : undefined,
  })) : [];

  const revenuePoints = useMemo(() => (revenue?.series || []).map((d) => ({ date: d.date, cents: d.cents })), [revenue]);

  return (
    <>
      <AdminPageHeader section="stats" meta="Platform totals, where each one comes from, and the rows behind it. Read-only by design." />
      {!stats ? <Card><Loading /></Card> : (
        <>
          <TileGrid label="MONEY" min="200px">
            {moneyTiles.map(([label, value, sub, key]) => (
              <StatTile key={label} label={label} value={value} sub={sub} tip={tipFor(key, label)} />
            ))}
          </TileGrid>
          <TileGrid label="PEOPLE + ACTIVITY" min="200px">
            {peopleTiles.map(([label, value, sub, key]) => (
              <StatTile key={label} label={label} value={value} sub={sub} tip={tipFor(key, label)} />
            ))}
          </TileGrid>

          <div className="flex flex-wrap items-center gap-2">
            <SegTrack ariaLabel="Range" value={days} onChange={setDays} options={RANGES.map((d) => ({ value: d, label: `${d}D` }))} />
            <span className="font-mono text-[11px] text-[#6B7280]">The two charts below follow this range. The tiles above are all time.</span>
          </div>

          <Card title={`MONEY IN · ${days} DAYS`} right={revenue ? `${money(revenue.totalCents)} IN THE RANGE` : null} bodyClass="px-5 pb-3">
            {!revenue ? <Loading /> : (
              <LineChart
                label="Money in over time"
                points={revenuePoints}
                series={[{ key: 'cents', label: 'MONEY IN', format: money }]}
                format={moneyAxis}
                source={revenue.source}
                drill="SEE THE EVENTS →"
                onDrill={(i) => setDrill({
                  title: `MONEY IN · ${shortDay(revenue.series[i].date)}`,
                  kicker: `${money(revenue.series[i].cents)} from ${revenue.series[i].count} ledger ${revenue.series[i].count === 1 ? 'line' : 'lines'}.`,
                  items: revenue.series[i].events,
                  round: false,
                })}
                breakdownFor={(i) => ({ breakdownLabel: 'BY CITY', breakdown: asBreakdown(revenue.series[i]?.byCity) })}
                axisInfo={{
                  title: 'MONEY IN',
                  kicker: 'PER DAY',
                  desc: 'Guest money arriving against an event — tickets, admissions and pledges, as the ledger recorded them. Not what Portal keeps.',
                  source: revenue.source,
                }}
                foot="A day with no bar had no ticket money, not no traffic."
              />
            )}
          </Card>

          <Card title={`NEW ACCOUNTS · ${days} DAYS`} right={signups ? `${fmt(signups.total)} IN THE RANGE` : null} bodyClass="px-3 pb-3">
            {!signups ? <div className="px-2"><Loading /></div> : (
              <ColumnChart
                height={180}
                items={(signups.series || []).map((d) => ({
                  key: d.date,
                  label: shortDay(d.date),
                  value: d.count,
                  tip: [['NEW ACCOUNTS', fmt(d.count)]],
                  breakdownLabel: 'HOW THEY ARRIVED',
                  breakdown: asBreakdown(d.byReferrer),
                  source: signups.source,
                  people: d.people,
                }))}
                drill="SEE THE PEOPLE →"
                onSelect={(it) => setDrill({
                  title: `SIGNED UP · ${it.label}`,
                  kicker: `${fmt(it.value)} new ${it.value === 1 ? 'account' : 'accounts'} that day.`,
                  items: it.people,
                })}
                axisInfo={{
                  title: 'NEW ACCOUNTS',
                  kicker: 'PER DAY',
                  desc: 'Accounts created that day. An invite card is a fact; anything else is the first referrer the tracker saw, and NOT TRACKED means we never saw one.',
                  source: signups.source,
                }}
              />
            )}
          </Card>

          <div className="grid gap-[14px]" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
            <Card title="EVENTS BY STATUS" right={canOpenEvents ? 'A ROW OPENS EVENTS FILTERED' : null} bodyClass="px-3 pb-3">
              <BarList items={statusRows} drill="OPEN IN EVENTS →" />
            </Card>

            <Card title="ESCROW BY EVENT" right={escrow ? `${money(escrow.totalCents)} HELD` : null} bodyClass="px-3 pb-3">
              {!escrow ? <div className="px-2"><Loading /></div> : (
                <BarList
                  items={(escrow.events || []).map((e) => ({
                    label: e.title,
                    count: e.cents,
                    tip: `${e.guests} ${e.guests === 1 ? 'guest' : 'guests'} · ${String(e.status).replace(/_/g, ' ')}${e.hostName ? ` · hosted by ${e.hostName}` : ''}`,
                    breakdownLabel: 'WHO PUT MONEY IN',
                    breakdown: asBreakdown(e.contributors),
                    source: escrow.source,
                    onSelect: canOpenEvents ? () => navigate(`/admin/events/${e.eventId}`) : undefined,
                  }))}
                  format={money}
                  drill="OPEN THE EVENT →"
                />
              )}
            </Card>

            <Card title="FEES BY SOURCE" right={fees ? `${money(fees.totalCents)} ALL TIME` : null} bodyClass="px-3 pb-3">
              {!fees ? <div className="px-2"><Loading /></div> : (
                <BarList
                  items={(fees.sources || []).map((f) => ({
                    label: f.label,
                    count: f.cents,
                    tip: `${f.desc} ${fmt(f.count)} ${f.count === 1 ? 'line' : 'lines'}.`,
                    source: f.source,
                  }))}
                  format={money}
                />
              )}
            </Card>
          </div>
        </>
      )}

      <AdminModal
        open={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.title || ''}
        kicker={drill?.kicker}
        width={560}
      >
        <DrillList
          items={(drill?.items || []).map((i) => ({
            label: i.label,
            display: i.display,
            // A person gets a round photo slot (initials when there is none); an event
            // row gets no photo at all rather than a grey square.
            ...(drill?.round === false ? {} : { image: null }),
            onClick: i.href ? () => { setDrill(null); openHref(i.href); } : undefined,
          }))}
          empty="NOTHING BEHIND THIS ONE."
        />
      </AdminModal>
    </>
  );
}
